import { useEffect, useRef } from 'react';
import {
  entityKey,
  setActiveNotificationTarget,
  useMarkNotificationViewedByTarget,
  useNotificationsByEntity,
} from '../notifications/index.js';

const CARGO_PROBLEM_VIEWING = new Set<'cargo_problem'>(['cargo_problem']);

/**
 * @aiPurpose marks the open cargo-problem notification aggregate viewed and
 * re-arms when realtime surfaces a newer aggregate for the same problem.
 */
export function useCargoProblemViewed(id: string) {
  const { mutate } = useMarkNotificationViewedByTarget();
  const { byEntity } = useNotificationsByEntity();
  const activatedRef = useRef(false);
  const seenCursorRef = useRef<string | null>(null);

  useEffect(() => {
    setActiveNotificationTarget({
      entityType: 'CARGO_PROBLEM',
      entityId: id,
      tabsViewing: CARGO_PROBLEM_VIEWING,
    });
    return () => setActiveNotificationTarget(null);
  }, [id]);

  useEffect(() => {
    const summary = byEntity.get(entityKey('CARGO_PROBLEM', id));
    const matches =
      summary?.unreadAggregates.filter((row) => row.notificationType === 'cargo_problem') ?? [];
    const newest =
      matches.length === 0
        ? null
        : matches.reduce((a, b) =>
            Date.parse(a.lastEventAt) >= Date.parse(b.lastEventAt) ? a : b,
          );
    const cursorAdvanced = newest !== null && seenCursorRef.current !== newest.id;
    if (activatedRef.current && !cursorAdvanced) return;
    if (newest !== null) seenCursorRef.current = newest.id;
    activatedRef.current = true;
    mutate({
      entityType: 'CARGO_PROBLEM',
      entityId: id,
      notificationType: 'cargo_problem',
    });
  }, [byEntity, id, mutate]);

  useEffect(
    () => () => {
      activatedRef.current = false;
      seenCursorRef.current = null;
    },
    [id],
  );
}
